import { IFile } from '@sdk/contracts';
import { Upload as BaseUpload, Button, Modal, Tooltip } from 'antd';
import { RcFile } from 'antd/lib/upload';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import {
  UploadedContainer,
  FileInfo,
  FileIconWrapper,
  FileName,
  ActionsWrapper
} from './UploadedFileView.styles';

import { Icon } from '@/components/Icon';
import { PdfViewer } from '@/components/PdfViewer';
import { getFileIcon, getIconColor } from '@/utils/fileIcon';

interface UploadedFileViewProps {
  uploadedFile: IFile;
  disabled?: boolean;
  acceptedTypes?: string[];
  onDelete: () => void;
  onUpload: (options: UploadRequestOption) => Promise<void>;
  beforeUpload: (file: RcFile, fileList: RcFile[]) => boolean;
  allowEdit?: boolean;
}

const ICON_SIZE = 16;

export const UploadedFileView: React.FC<UploadedFileViewProps> = ({
  uploadedFile,
  disabled,
  acceptedTypes = [],
  onDelete,
  onUpload,
  beforeUpload,
  allowEdit = true
}) => {
  const theme = useTheme();
  const { t } = useTranslation('DefaultTranslations');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fileIcon = getFileIcon(uploadedFile.name);
  const iconColor = getIconColor(theme, fileIcon);

  const isPdf = uploadedFile.name.toLowerCase().endsWith('.pdf');

  const handleFileClick = () => {
    if (isPdf && uploadedFile.url) {
      setIsModalOpen(true);
    } else {
      handleDownload();
    }
  };

  const handleDownload = async () => {
    if (!uploadedFile.url) return;

    try {
      const response = await fetch(uploadedFile.url);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const dummyAnchor = document.createElement('a');
      dummyAnchor.href = objectUrl;
      dummyAnchor.download = uploadedFile.name;
      document.body.appendChild(dummyAnchor);
      dummyAnchor.click();
      document.body.removeChild(dummyAnchor);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <UploadedContainer>
        <FileInfo onClick={handleFileClick}>
          <FileIconWrapper $iconColor={iconColor}>
            <Icon i={fileIcon} size={ICON_SIZE} />
          </FileIconWrapper>
          <FileName>{uploadedFile.name}</FileName>
        </FileInfo>
        <ActionsWrapper>
          {allowEdit && (
            <Tooltip title={t('DELETE_FILE')}>
              <Button type="text" onClick={onDelete} disabled={disabled}>
                <Icon i="TrashCan" size={ICON_SIZE} color="red7" />
              </Button>
            </Tooltip>
          )}
          <Tooltip title={t('VIEW_FILE')}>
            <Button type="text" onClick={handleFileClick}>
              <Icon i="View" size={ICON_SIZE} />
            </Button>
          </Tooltip>
          {allowEdit && (
            <BaseUpload
              customRequest={onUpload}
              showUploadList={false}
              beforeUpload={beforeUpload}
              disabled={disabled}
              accept={acceptedTypes.join(',')}>
              <Tooltip title={t('REPLACE_FILE')}>
                <Button type="text">
                  <Icon i="Edit" size={ICON_SIZE} />
                </Button>
              </Tooltip>
            </BaseUpload>
          )}
        </ActionsWrapper>
      </UploadedContainer>

      {isPdf && uploadedFile.url && (
        <Modal
          open={isModalOpen}
          onCancel={handleCloseModal}
          width="90%"
          footer={
            <Button type="primary" onClick={handleCloseModal}>
              {t('CLOSE')}
            </Button>
          }
          centered>
          <PdfViewer url={uploadedFile.url} height={600} />
        </Modal>
      )}
    </>
  );
};
